'use client';

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useDeviceType } from '@/hooks/useIsMobile';
import ImageAdaptive from '@/components/ImageAdaptive';

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    pcWidth?: string;
    mbWidth?: string;
    pcHeight?: string;
    mbHeight?: string;
    direction?: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'clip';
    borderColor?: string;
    zIndex?: number;
}

export default function Modal({
    isOpen,
    onClose,
    children,
    pcWidth = '100%',
    mbWidth = '100%',
    pcHeight = '100%',
    mbHeight = '100%',
    direction = 'center',
    borderColor = '',
    zIndex = 80,
}: ModalProps) {
    const { isMobile } = useDeviceType();
    const modalWidth = isMobile ? mbWidth : pcWidth;
    const modalHeight = isMobile ? mbHeight : pcHeight;

    const [modalContainer, setModalContainer] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setModalContainer(document.body);
    }, []);
    if (!modalContainer) return null;

    const initialMap: Record<string, any> = {
        top: { y: -100, opacity: 0 },
        bottom: { y: 100, opacity: 0 },
        left: { x: -100, opacity: 0 },
        right: { x: 100, opacity: 0 },
        center: { opacity: 0 },
        clip: { clipPath: 'inset(0% 0% 100% 0%)', opacity: 0 },
    };

    const animateMap: Record<string, any> = {
        top: { y: 0, opacity: 1 },
        bottom: { y: 0, opacity: 1 },
        left: { x: 0, opacity: 1 },
        right: { x: 0, opacity: 1 },
        center: { opacity: 1 },
        clip: { clipPath: 'inset(0% 0% 0% 0%)', opacity: 1 },
    };

    const exitMap: Record<string, any> = {
        top: { y: -100, opacity: 0 },
        bottom: { y: 100, opacity: 0 },
        left: { x: -100, opacity: 0 },
        right: { x: 100, opacity: 0 },
        center: { opacity: 0 },
        clip: { clipPath: 'inset(0% 0% 100% 0%)', opacity: 0 },
    };

    return createPortal(
        <AnimatePresence mode="wait">
            {isOpen && (
                <motion.div
                    className="fixed inset-0 flex items-center justify-center"
                    style={{ zIndex }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="absolute inset-0 backdrop-blur-sm bg-color-base-2/40" onClick={onClose} />
                    <div className="relative z-[100]" style={{ width: modalWidth, height: modalHeight }}>
                        <div className="absolute -top-4 -right-4 z-[100]">
                            <ImageAdaptive
                                src="/images/modal/close.svg"
                                baseWidth={20}
                                className="cursor-pointer"
                                onClick={onClose}
                            />
                        </div>

                        <motion.div
                            className={`bg-white rounded-2xl shadow-lg w-full h-full overflow-hidden p-0 ${
                                borderColor ? `border ${borderColor}` : ''
                            }`}
                            initial={initialMap[direction]}
                            animate={animateMap[direction]}
                            exit={exitMap[direction]}
                            transition={{ duration: 0.5, ease: 'easeOut' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="overflow-y-auto w-full h-full p-2 bg-color-white-base">{children}</div>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>,
        modalContainer,
    );
}
